/*
 * © 2026 Ecsper Ltd. All rights reserved.
 * SPDX-License-Identifier: LicenseRef-Ecsper-Proprietary
 * Unauthorised use, copying, or distribution is strictly prohibited.
 */

/**
 * Synchronous or asynchronous request interceptor. Receives the assembled
 * `Request` immediately before fetch is invoked and returns the (possibly
 * mutated or replaced) `Request` to send.
 *
 * Interceptors run in the order supplied to `EcsperClientConfig`. They are
 * the integration point for cross-cutting concerns such as logging, request
 * tagging, and (in Stage 5.5+) telemetry duration measurement.
 */
export interface RequestInterceptor {
  (request: Request): Request | Promise<Request>;
}

/**
 * Synchronous or asynchronous response interceptor. Receives the raw fetch
 * `Response` and the original `Request` and returns the (possibly mutated or
 * replaced) `Response` for downstream parsing.
 *
 * Response interceptors run before status-code parsing, so they observe both
 * 2xx and non-2xx responses.
 */
export interface ResponseInterceptor {
  (response: Response, request: Request): Response | Promise<Response>;
}

/** Run every request interceptor in order, returning the final `Request`. */
export async function runRequestInterceptors(
  request: Request,
  interceptors: readonly RequestInterceptor[],
): Promise<Request> {
  let current = request;
  for (const interceptor of interceptors) {
    current = await interceptor(current);
  }
  return current;
}

/** Run every response interceptor in order, returning the final `Response`. */
export async function runResponseInterceptors(
  response: Response,
  request: Request,
  interceptors: readonly ResponseInterceptor[],
): Promise<Response> {
  let current = response;
  for (const interceptor of interceptors) {
    current = await interceptor(current, request);
  }
  return current;
}
